import { useState } from 'react';
import { PagesClient, PagesDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

export const usePagesApi = () => {
    const apiClient = new PagesClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: PagesDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createPage(data);
        } catch (err: any) {
            setError(err.message || 'Error creating Page');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: PagesDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updatePage(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating Page');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getPageById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching Page');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchPages(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Pages');
        } finally {
            setIsLoading(false);
        }
    };

    // Re-parents a page via drag-and-drop. parentPageId null moves it to the top level.
    // The backend recomputes the slug paths of the page and its descendants.
    const movePage = async (id: string, parentPageId: string | null) => {
        setIsLoading(true);
        try {
            return await apiClient.movePage(id, { parentPageId: parentPageId ?? undefined });
        } catch (err: any) {
            setError(err.message || 'Error moving Page');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deletePage(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting Page');
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, movePage, remove, isLoading, error };
};
